import { ApiError, type ProblemDetails, Problems } from "@zuplo/errors";
import type { Context, ErrorHandler, NotFoundHandler } from "hono";
import { HTTPException } from "hono/http-exception";
import type { ContentfulStatusCode } from "hono/utils/http-status";
import type { CommonLogger } from "./interfaces.js";

/**
 * Sends an RFC 7807 problem document. Every error response from the editor
 * server goes through here so failures look the same as they do coming from the
 * real Management API.
 *
 * The media type is deliberately `application/json` rather than the RFC's
 * `application/problem+json`: local mode exists to behave like the Management
 * API, and that is what the API — and this server before it moved to Hono —
 * sends for these. The test-proxy route is the exception, and sets
 * `application/problem+json` because the real test-proxy does.
 */
function sendProblem(c: Context, problem: ProblemDetails): Response {
  problem.trace = {
    timestamp: new Date().toJSON(),
    requestId: c.req.header("x-request-id") ?? null,
  };

  return c.body(
    JSON.stringify(problem, null, 2),
    problem.status as ContentfulStatusCode,
    {
      "content-type": "application/json",
    },
  );
}

/**
 * Translates anything thrown by a route into a problem document. Note that the
 * `Problems` entries are constants, so they're always copied before the
 * request-specific fields are filled in.
 */
export function createErrorHandler(logger: CommonLogger): ErrorHandler {
  return (error, c) => {
    let problem: ProblemDetails;

    if (error instanceof ApiError) {
      problem = {
        // Fields the thrower added beyond the standard ones — e.g. the `errors`
        // list on a validation failure — arrive here as `extra`.
        ...error.extra,
        type: error.type,
        title: error.message,
        status: error.status,
        detail: error.detail,
        instance: error.instance,
      };
    } else if (error instanceof HTTPException) {
      // Thrown by Hono itself, e.g. a malformed JSON request body.
      problem = {
        ...getProblemFromStatus(error.status),
        detail: error.message,
      };
    } else {
      const status = getStatusFromError(error);
      problem = getProblemFromStatus(status >= 400 ? status : 500);

      if (typeof error === "string") {
        problem.detail = error;
      } else if (error && typeof error === "object" && "message" in error) {
        problem.detail = String(error.message);
      }
    }

    const context = { err: error, method: c.req.method, path: c.req.path };
    if (problem.status < 500) {
      logger.warn(context, problem.title);
    } else {
      logger.error(context, problem.title);
    }

    return sendProblem(c, problem);
  };
}

/**
 * Unmatched routes get a problem document too, rather than Hono's plain-text
 * "404 Not Found".
 */
export function createNotFoundHandler(): NotFoundHandler {
  return (c) => sendProblem(c, { ...Problems.NOT_FOUND });
}

/** Best effort at a status code for errors that aren't ours. */
function getStatusFromError(error: unknown): number {
  if (!error || typeof error !== "object") {
    return 500;
  }
  if ("statusCode" in error && typeof error.statusCode === "number") {
    return error.statusCode;
  }
  if ("status" in error && typeof error.status === "number") {
    return error.status;
  }
  return 500;
}

function getProblemFromStatus(status: number): ProblemDetails {
  if (status === 401) {
    return { ...Problems.UNAUTHORIZED };
  } else if (status === 403) {
    return { ...Problems.FORBIDDEN };
  } else if (status === 404) {
    return { ...Problems.NOT_FOUND };
  } else if (status === 405) {
    return { ...Problems.METHOD_NOT_ALLOWED };
  } else if (status < 500 && status >= 400) {
    return { ...Problems.BAD_REQUEST, status };
  }
  return { ...Problems.INTERNAL_SERVER_ERROR };
}
